/*********************************************************
 * Copyright (C) 2018 VMware, Inc. All rights reserved.
 *********************************************************/

/**
 * Store device preference for RTAV, per chromebook deivce.
 *
 * Choose id for device identity since it's unique and consistent
 * and is not an optional property.
 * Clearable, same among users.
 */

import Logger from '../../../core/libs/logger';
import { Injectable } from "@angular/core";
import { clientUtil } from "@html-core";

@Injectable({
   providedIn: "root"
})

export class PreferredRTAVDeviceService {
   public static readonly preferredDeviceKeyProfix = "preferredDeviceId_";
   public static readonly deviceTypeMap = {
      audio: "audioinput",
      video: "videoinput",
      audioOut: "audiooutput"
   };

   constructor() {}

   /**
    * @param  {[string]} key
    * @return {Sting} Return stored string or undefined
    */
   public readKey = (key) => {
      return new Promise((resolve, reject) => {
         try {
            chrome.storage.local.get(key, (obj) => {
               if (chrome.runtime.lastError) {
                  Logger.error("chrome LastError = " + chrome.runtime.lastError);
                  resolve(undefined);
                  return;
               }
               if (!obj) {
                  resolve(undefined);
                  return;
               }
               resolve(obj[key]);
            });
         } catch (e) {
            Logger.exception(e);
            resolve(undefined);
         }
      });
   }

   /**
    * Should catch exception if call this function
    */
   private setKey = (key, value) => {
      return new Promise((resolve, reject) => {
         try {
            let obj = {};
            obj[key] = value;
            chrome.storage.local.set(obj, () => {
               if (chrome.runtime.lastError) {
                  let error = chrome.runtime.lastError;
                  reject(error);
                  return;
               }
               // @ts-ignore
               resolve();
            });
         } catch (e) {
            reject(e);
         }
      });
   };

   /**
    * Async returns the device lists for audio in and video in devices
    */
   private getDeviceList = () => {
      return new Promise((resolve, reject) => {
         let Lists = {
            audio: [],
            video: [],
            audioOut: []
         };
         let index = {
            audio: 0,
            video: 0,
            audioOut: 0
         };


         let getDeviceName = function(label, type) {
            if (label) {
               return label;
            }
            let name = type + index[type];
            index[type]++;
            return name;
         };

         let pushInfo = function(type, device) {
            Lists[type].push({
               name: getDeviceName(device.label, type),
               id: device.deviceId
            });
         };

         navigator.mediaDevices.enumerateDevices().then((devices) => {
            devices.forEach((device) => {
               switch (device.kind) {
                  case PreferredRTAVDeviceService.deviceTypeMap["audio"]:
                     pushInfo("audio", device);
                     break;
                  case PreferredRTAVDeviceService.deviceTypeMap["video"]:
                     pushInfo("video", device);
                     break;
                  case PreferredRTAVDeviceService.deviceTypeMap["audioOut"]:
                     pushInfo("audioOut", device);
                     break;
               }
            });
            resolve(Lists);
         });
      });
   };

   /**
    * @return {Boolean} Whether current platform can support device choosing.
    */
   public isDeviceChoosingSupported = () => {
      try {
         return !!chrome.storage.local && !!chrome.runtime &&
            !!navigator.mediaDevices.enumerateDevices;
      } catch (e) {
         return false;
      }
   };

   /**
    * Don't use device name, but using id, so this function will return null
    * for existing device after user clear cookies.
    *
    * Don't use async to avoid a bug.
    *
    * @param  {string} type Of string "audio" or "video"
    * @return {string} Returns the deviceId, or return null if can't find the
    *    preferred device.
    */
   public getPreferredDeviceId = (type) => {
      return new Promise((resolve, reject) => {
         if (!this.isDeviceChoosingSupported()) {
            resolve(null);
            return;
         }
         this.readKey(PreferredRTAVDeviceService.preferredDeviceKeyProfix + type).then((preferredDeviceId) => {
            if (preferredDeviceId) {
               navigator.mediaDevices.enumerateDevices().then((devices) => {
                  let deviceExist = devices.some((device) => {
                     if (device.kind === PreferredRTAVDeviceService.deviceTypeMap[type] &&
                        device.deviceId === preferredDeviceId) {
                        return true;
                     }
                     return false;
                  });
                  if (deviceExist) {
                     resolve(preferredDeviceId);
                  } else {
                     resolve(null);
                  }
               });
            } else {
               resolve(null);
            }
         });
      });
   }

   public setPreferredDeviceId = async (type, id) => {
      if (!this.isDeviceChoosingSupported()) {
         Logger.error("can't set setPreferredDeviceId " +
            "if device choosing is not supported");
         return;
      }
      await this.setKey(PreferredRTAVDeviceService.preferredDeviceKeyProfix + type, id);
   };

   /**
    * Return Info for displaying
    * Don't use async to avoid a bug
    */
   public getDeviceInfo = () => {
      return new Promise((resolve, reject) => {
         if (!this.isDeviceChoosingSupported()) {
            resolve(null);
            return;
         }
         Promise.all([this.getDeviceList(), this.readKey(PreferredRTAVDeviceService.preferredDeviceKeyProfix + "audio"),
               this.readKey(PreferredRTAVDeviceService.preferredDeviceKeyProfix + "video"),
               this.readKey(PreferredRTAVDeviceService.preferredDeviceKeyProfix + "audioOut")
            ])
            .then((result: any) => {
               resolve({
                  audio: result[0].audio,
                  video: result[0].video,
                  //now we only have two options for preferred speakr: 'all' and 'default speaker'
                  audioOut: result[0].audioOut,
                  preferredIds: {
                     audio: result[1],
                     video: result[2],
                     audioOut: result[3]
                  }
               });
            }).catch((e) => {
               reject(e);
            });
      });
   }
}